import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { TFunction, useTranslation } from 'react-i18next'
import {
  ActionList,
  ActionListChildren,
  Avatar,
  Box,
  Button,
  Highlight,
  Icon,
  useSidePanel,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ContactCase } from 'types/contacts'
import { PaymentContextType } from 'types/payments'
import { ContactCaseListIcon } from './components/ContactCaseListIcon'
import { ContactCaseSummary } from './components/ContactCaseSummary'

interface ContactCaseListProps {
  contactCases: ContactCase[]
  handleAssignContactClick: (contactCase: ContactCase) => void
}

const getHighlightDescription = (
  paymentContextType: PaymentContextType,
  t: TFunction<'translation', undefined>
) => {
  switch (paymentContextType) {
    case PaymentContextType.CHANNEL_PAYMENT:
      return t('contacts.channelPayment')
    case PaymentContextType.PAYMENT_IN:
      return t('contacts.cryptoPayment')
    case PaymentContextType.DEPOSIT:
      return t('contacts.directDeposit')
    default:
      return
  }
}

export const ContactCaseList = ({
  contactCases,
  handleAssignContactClick,
}: ContactCaseListProps) => {
  const { t } = useTranslation()

  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.payments
  const canEdit = permissions?.canEdit

  const {
    isSidePanelOpen: isContactCaseDetailsOpen,
    sidePanelData: contactCase,
    paging: pageContactCases,
    setIsSidePanelOpen: setIsContactCaseDetailsOpen,
    updateSidePanel: updateContactCaseDetails,
  } = useSidePanel<ContactCase>(contactCases)

  const onDetailsClick = (contactCaseIndex: number) => {
    updateContactCaseDetails(contactCaseIndex)
    setIsContactCaseDetailsOpen(true)
  }

  const actionList: ActionListChildren[] = useMemo(() => {
    return contactCases?.map((contactCase, contactCaseIndex) => {
      const {
        hash,
        amount,
        currencyCode,
        network,
        paymentCreated,
        paymentContext: { type: paymentContextType } = {},
      } = contactCase || {}

      const title = `${amount} ${currencyCode}(${network})`

      const headerHighlightLeft = (
        <Highlight
          iconElement={<ContactCaseListIcon paymentContextType={paymentContextType} />}
          type="feature"
          variant="emphasis"
        >
          {getHighlightDescription(paymentContextType, t)}
        </Highlight>
      )

      return {
        id: hash,
        key: hash,
        title: title,
        transparent: false,
        HeaderHighlightLeft: headerHighlightLeft,
        Avatar: <Avatar type="currency" name={currencyCode ? currencyCode?.toLowerCase() : ''} />,
        BodyPre: dayjs(paymentCreated).format(DateFormat.DD_MMM_YYYY_HH_MM_A),
        CustomAction: (
          <Button
            disabled={!canEdit || viewOnly}
            iconElement={<Icon name="UserCirclePlus" />}
            variant="ghost"
            onClick={() => handleAssignContactClick(contactCase)}
          >
            {t('contacts.assignContact')}
          </Button>
        ),
        border: true,
        onItemClick: () => onDetailsClick(contactCaseIndex),
      }
    })
  }, [contactCases])
  return (
    <Box>
      <Box paddingB={16}>
        <ActionList id="contact-case-list-action-list" items={actionList} />
      </Box>

      <ContactCaseSummary
        isOpen={isContactCaseDetailsOpen}
        contactCase={contactCase}
        pageBeneficiaries={pageContactCases}
        onClose={() => setIsContactCaseDetailsOpen(false)}
      />
    </Box>
  )
}
